import { useState } from 'react'
import { router, useForm } from '@inertiajs/react'
import HeartButton from './HeartButton'
import Avatar from './Avatar'

function EditForm({ photo, onDone }) {
  const { data, setData, patch, processing, errors } = useForm({
    caption: photo.caption || '',
    taken_on: photo.takenOn,
    favorite: photo.favorite,
  })

  function submit(event) {
    event.preventDefault()
    patch(`/photos/${photo.id}`, { preserveScroll: true, onSuccess: onDone })
  }

  return (
    <form onSubmit={submit} className="space-y-3 p-4">
      <input
        type="text"
        value={data.caption}
        onChange={(event) => setData('caption', event.target.value)}
        maxLength={200}
        placeholder="Caption"
        className="input-cute"
        autoFocus
      />
      {errors.caption && <p className="text-sm font-bold text-rose-600">{errors.caption}</p>}

      <input
        type="date"
        value={data.taken_on}
        onChange={(event) => setData('taken_on', event.target.value)}
        className="input-cute"
      />

      <label className="flex cursor-pointer items-center gap-2 text-sm font-bold text-ink-soft">
        <input
          type="checkbox"
          checked={data.favorite}
          onChange={(event) => setData('favorite', event.target.checked)}
          className="h-4 w-4 cursor-pointer accent-blush-400"
        />
        Favourite ⭐️
      </label>

      <div className="flex gap-2">
        <button type="submit" disabled={processing} className="btn-cute flex-1 bg-blush-400 text-sm text-white">
          Save
        </button>
        <button type="button" onClick={onDone} className="btn-cute border-2 border-blush-100 bg-white text-sm text-ink-soft">
          Cancel
        </button>
      </div>
    </form>
  )
}

export default function PhotoCard({ photo, onOpen, priority = false }) {
  const [editing, setEditing] = useState(false)
  // A poster frame can be missing (no ffmpeg) or fail to generate (a clip ffmpeg can't decode).
  // Either way, fall back to the placeholder tile rather than showing a broken image.
  const [thumbFailed, setThumbFailed] = useState(false)
  const [thumbLoaded, setThumbLoaded] = useState(false)

  function destroy(event) {
    event.stopPropagation()
    if (!confirm('Remove this photo from the album?')) return
    router.delete(`/photos/${photo.id}`, { preserveScroll: true })
  }

  return (
    <div className="card-cute animate-pop-in break-inside-avoid overflow-hidden transition hover:-translate-y-1 hover:shadow-xl sm:mb-5">
      <div className="relative cursor-zoom-in" onClick={editing ? undefined : onOpen}>
        {photo.thumbUrl && !thumbFailed ? (
          <>
            {/* The tile already reserves its space via the aspect ratio, so this just fills it
                with something calm until the photo arrives. */}
            {!thumbLoaded && (
              <span className="absolute inset-0 animate-pulse bg-gradient-to-br from-blush-100 via-lilac-baby/50 to-sky-baby/50" />
            )}
            <img
            src={photo.thumbUrl}
            alt={photo.caption || (photo.kind === 'video' ? 'Baby video' : 'Baby photo')}
            // Lazy-loading the photos already on screen just delays the first paint, so the
            // opening card or two are fetched eagerly and at high priority instead.
            loading={priority ? 'eager' : 'lazy'}
            fetchPriority={priority ? 'high' : 'auto'}
            decoding="async"
            onError={() => setThumbFailed(true)}
            onLoad={() => setThumbLoaded(true)}
            // Fixed ratio on phones so tiles line up in a grid; desktop keeps the
            // natural ratio for the masonry columns.
            className={`aspect-[4/5] w-full object-cover transition-opacity duration-300 sm:aspect-auto ${
              thumbLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
          </>
        ) : (
          // A video with no poster frame — show a friendly tile rather than a broken image.
          <div className="flex aspect-[4/5] w-full items-center justify-center bg-lilac-baby/60 sm:aspect-video">
            <span className="text-5xl" aria-hidden="true">
              {photo.kind === 'video' ? '🎬' : '🖼️'}
            </span>
          </div>
        )}

        {photo.kind === 'video' && (
          <span className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-ink/55 text-lg text-white shadow-lg backdrop-blur-sm sm:h-14 sm:w-14 sm:text-2xl">
              ▶
            </span>
          </span>
        )}

        {photo.favorite && (
          <span className="absolute top-2 right-2 rounded-full border-2 border-white bg-lemon-baby px-2 py-0.5 font-display text-xs font-bold text-amber-800 shadow sm:top-3 sm:right-3 sm:px-3 sm:py-1 sm:text-sm">
            ⭐️ Favourite
          </span>
        )}

        <div className="absolute bottom-2 left-2 sm:bottom-3 sm:left-3">
          <HeartButton photo={photo} />
        </div>
      </div>

      {editing ? (
        <EditForm photo={photo} onDone={() => setEditing(false)} />
      ) : (
        <div className="p-3 sm:p-4">
          {photo.caption && (
            <p className="font-display text-base leading-snug font-bold text-ink sm:text-lg">
              {photo.caption}
            </p>
          )}

          <div className="mt-2 flex items-center gap-2 sm:mt-3">
            <Avatar initials={photo.uploader.initials} size="sm" />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-ink-soft">
                {photo.uploader.name}
                {photo.uploader.relation ? ` · ${photo.uploader.relation}` : ''}
              </p>
              <p className="text-xs text-ink-soft/70">{photo.monthLabel}</p>
            </div>

            {photo.editable && (
              <div className="ml-auto flex gap-1">
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  aria-label="Edit photo"
                  className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-blush-50 sm:h-9 sm:w-9"
                >
                  ✏️
                </button>
                <button
                  type="button"
                  onClick={destroy}
                  aria-label="Delete photo"
                  className="inline-flex h-11 w-11 cursor-pointer items-center justify-center rounded-full transition hover:bg-rose-50 sm:h-9 sm:w-9"
                >
                  🗑️
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
